import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import useColors from "../../../api/hooks/useColors";
import { UserPlatformAccessRole } from "../../../utils/constants";

const ChangeAccessRoleDialog = ({
  isDialogOpen,
  handleDialogClose,
  handleConfirmRoleChange,
  role,
}) => {
  const colors = useColors();

  const roleDescriptions = {
    [UserPlatformAccessRole.VIEWER]: {
      includes: [
        "Connect or disconnect integrations if they own the specific integration.",
      ],
      excludes: [
        "Ability to perform any other actions within the system.",
        "Ability to manage integrations, roles, ownership, or settings.",
      ],
    },
    [UserPlatformAccessRole.SUPPORT]: {
      includes: [
        "Full view access to all platform pages and controls.",
        "Unlimited access to view controls and remediation plans without POC restrictions.",
        "Ability to view all security posture information for support purposes.",
      ],
      excludes: [
        "Ability to modify or take actions on controls, integrations, or settings.",
        "Ability to manage users, roles, or permissions.",
        "Ability to create, edit, or delete any system configurations.",
      ],
    },
    [UserPlatformAccessRole.ADMIN]: {
      includes: [
        "Full access to system settings and configurations.",
        "Ability to manage users, roles, and ownership assignments.",
        "Full control over integrations, including connecting and disconnecting tools.",
        "Ability to perform all actions within the system.",
      ],
      excludes: [],
    },
    [UserPlatformAccessRole.USER]: {
      includes: [
        "Ability to perform actions within the system.",
        "Ability to connect and manage integrations.",
        "Ability to manage ownership of specific tools.",
      ],
      excludes: [
        "Access to system settings and configurations.",
        "Ability to manage other users, roles, or permissions.",
      ],
    },
    [UserPlatformAccessRole.LIMITED_VIEWER]: {
      includes: [
        "Ability to view and take action on tools they’ve been assigned ownership of by their manager, within the Controls, Remediation, and Inventory sections of the platform.",
        "Ability to view the full inventory, but act only on their owned tools.",
      ],
      excludes: [
        "Access to other areas of the platform.",
        "Ability to manage or take action on tools they do not own.",
      ],
    },
  };

  const roleDetails = roleDescriptions[role] || { includes: [], excludes: [] };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleDialogClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
          padding: "12px",
          maxWidth: "630px",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ padding: "10px 0px 0px 12px" }}
      >
        <DialogTitle sx={{ padding: "0px" }}>Confirm Role Change</DialogTitle>
        <Box onClick={handleDialogClose} padding={1} sx={{ cursor: "pointer" }}>
          <ClearRoundedIcon style={{ color: colors.action_active }} />
        </Box>
      </Box>

      <DialogContent sx={{ width: "100%", paddingLeft: "12px" }}>
        <DialogContentText>
          <Typography
            variant="body1"
            color={colors.text_secondary}
            marginBottom="22px"
            noWrap
          >
            Are you sure you want to change this member's role to{" "}
            <strong>{role}</strong>?
          </Typography>
          {roleDetails.includes.length > 0 && (
            <>
              <Typography
                variant="body1"
                color={colors.text_secondary}
                fontWeight="bold"
              >
                What this role includes:
              </Typography>
              <ul style={{ paddingLeft: "25px" }}>
                {roleDetails.includes.map((item, index) => (
                  <li key={index}>
                    <Typography
                      variant="body1"
                      color={colors.text_secondary}
                      marginBottom={0.2}
                    >
                      {item}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
          {roleDetails.excludes.length > 0 && (
            <>
              <Typography
                variant="body1"
                color={colors.text_secondary}
                fontWeight="bold"
              >
                What this role excludes:
              </Typography>
              <ul style={{ paddingLeft: "25px" }}>
                {roleDetails.excludes.map((item, index) => (
                  <li key={index}>
                    <Typography variant="body1" marginBottom={0.2}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleDialogClose}
          sx={{
            textTransform: "none",
            color: colors.text_primary,
          }}
        >
          <Typography variant="body2">Cancel</Typography>
        </Button>
        <Button
          onClick={handleConfirmRoleChange}
          autoFocus
          sx={{
            color: colors.primary_main,
            textTransform: "none",
            "&.Mui-disabled": {
              color: colors.text_disabled,
            },
          }}
        >
          <Typography variant="body2">Confirm</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeAccessRoleDialog;
